import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Quote } from 'lucide-react';

const TestimonialsPreview: React.FC = () => {
  const testimonials = [
    {
      name: 'Mr. Asmon Fredrick',
      role: 'CEO',
      company: 'Aamisha Foods Pvt Ltd',
      content: 'An excellent design agency with a helpful team who really understand customer requirements and work alongside us to achieve our KRA\'s.',
    },
    {
      name: 'Mr. Darwin Samuel',
      role: 'Business Owner',
      company: 'Abu Dhabi, UAE',
      content: 'Loved working with CDBS! Their designs, digital marketing strategy, and overall support have helped our business grow. Super friendly team and great results!',
    },
    {
      name: 'Ms. Tanvi Dua',
      role: 'Business Owner',
      company: 'Oslo, Norway',
      content: 'Color Drop Business Solutions delivers exceptional digital marketing and creative services. Their team is skilled, responsive, and truly understands business needs.',
    },
  ];

  // const stats = [
  //   { value: '98%', label: 'Client Satisfaction' },
  //   { value: '500+', label: 'Projects Completed' },
  //   { value: '50+', label: 'Luxury Brands Served' },
  //   { value: '5★', label: 'Average Rating' },
  // ];

  const stats = [
    { value: '1365+', label: 'Ad Campaigns Done' },
    { value: '18+', label: 'Location Served' },
    { value: '12+', label: 'Years Experience' },
    { value: '560', label: 'Satisfied Clients' },
  ];

  return (
    <section className="py-24 bg-white">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: false }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6">
            What Our <span className="text-gradient">Clients Say</span>
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed">
            Don't just take our word for it. Here's what luxury brands say about working with us.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: false }}
              className="group"
            >
              <div className="bg-neutral-50 rounded-2xl p-8 h-full hover:shadow-lg transition-all duration-300 border border-neutral-100 group-hover:border-primary-200 flex flex-col justify-between">
                {/* Quote Icon */}
                <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center mb-6">
                  <Quote className="w-6 h-6 text-white" />
                </div>

                {/* Content */}
                <p className="text-neutral-700 mb-6 leading-relaxed italic">
                  "{testimonial.content}"
                </p>

                {/* Author */}
                <div className="flex items-center pt-4 border-t border-neutral-100">
                  {/* Google Logo */}
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 shadow-sm border border-neutral-200">
                    <svg className="w-7 h-7" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                  </div>

                  {/* Author Info */}
                  <div className="flex-grow">
                    <div className="font-semibold text-neutral-900">{testimonial.name}</div>
                    <div className="text-sm text-neutral-600">{testimonial.role}, {testimonial.company}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: false }}
          className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl p-8 md:p-12 mb-16"
        >
          <div className="text-center mb-8">
            <h3 className="text-3xl font-serif font-bold text-white mb-4">
              Trusted by Industry Leaders
            </h3>
            <p className="text-white/90 max-w-2xl mx-auto">
              Our track record speaks for itself. We've helped hundreds of luxury brands achieve their digital goals.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-white/90 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Featured Testimonial */}
        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: false }}
          className="bg-neutral-50 rounded-2xl p-8 md:p-12 text-center"
        >
          <div className="max-w-4xl mx-auto">
            <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Quote className="w-8 h-8 text-white" />
            </div>
            
            <blockquote className="text-2xl md:text-3xl font-serif font-medium text-neutral-900 mb-8 leading-relaxed">
              "Color Drop doesn't just build websites and apps – they craft digital experiences that elevate your brand and drive real business results. Their understanding of luxury branding is exceptional."
            </blockquote>
            
            <div className="flex items-center justify-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">AJ</span>
              </div>
              <div className="text-left">
                <div className="font-semibold text-lg text-neutral-900">Alexandra Thompson</div>
                <div className="text-neutral-600">CEO, Premium Lifestyle Brand</div>
              </div>
            </div>
          </div>
        </motion.div> */}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: false }}
          className="text-center mt-16"
        >
          <Link to="/testimonials" className="btn-primary inline-flex items-center">
            Read More Testimonials
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsPreview;
